/**
 * Outil « Cercle et équation » (Boîte à outils, 1re → Tle).
 *
 * « Centre et rayon » : on déplace le centre Ω et un point P du cercle ;
 * l'équation (x − a)² + (y − b)² = r² s'écrit en direct, et sa forme
 * développée se ramène à la canonique en complétant les carrés. Un point M
 * mobile est dedans, dessus ou dehors selon (xM − a)² + (yM − b)² comparé à
 * r². « Diamètre [AB] » : M est sur le cercle exactement quand MA · MB = 0
 * (angle droit en M). Défis. Calculs : `circle.ts`.
 */

import { useRef, useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { makeFrame, snapPoint, type Pt } from '../../ui/frame';
import { formatCoords, formatNumber } from '../../ui/format';
import {
  DragHandle,
  FIGURE_BG_CLASS,
  HaloText,
  PlotGrid,
  RightAngleMark,
} from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  canonicalPlain,
  canonicalTex,
  completingSteps,
  dec,
  diameterTex,
  fromDiameter,
  GOALS,
  position,
  radiusExact,
} from './circle';

type Mode = 'center' | 'diameter';
type Name = 'O' | 'P' | 'A' | 'B' | 'M';

const SIZE = 320;
const VP = { xMin: -7.5, xMax: 7.5, yMin: -7.5, yMax: 7.5 };
const frame = makeFrame(VP, SIZE);
const CIRCLE = 'var(--color-primary-500)';
const CENTER = 'var(--color-ink-900)';
const POS_COLOR = {
  inside: 'var(--color-success-600)',
  on: 'var(--color-accent-700)',
  outside: 'var(--color-error-500)',
} as const;
/** même code couleur, contrasté pour du texte */
const POS_TEXT_CLASS = {
  inside: 'text-success-600',
  on: 'text-accent-700',
  outside: 'text-error-600',
} as const;
/** « (3 - 2)^2 », « (3 + 1)^2 », « (3)^2 » */
const diff = (u: number, a: number) =>
  a === 0 ? `(${dec(u)})^2` : `(${dec(u)} ${a < 0 ? '+' : '-'} ${dec(Math.abs(a))})^2`;
const POS_TEXT = { inside: 'à l’intérieur du', on: 'sur le', outside: 'à l’extérieur du' } as const;

export function CircleEquationTool({ onSend, sendCost }: ToolProps) {
  const [mode, setMode] = useState<Mode>('center');
  const [pts, setPts] = useState<Record<Name, Pt>>({
    O: { x: 1, y: 1 },
    P: { x: 4, y: 1 },
    A: { x: -3, y: -1 },
    B: { x: 3, y: 1 },
    M: { x: 0, y: 3 },
  });
  const [developedOn, setDevelopedOn] = useState(false);
  const [goal, setGoal] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const ids: readonly Name[] = mode === 'center' ? ['O', 'P', 'M'] : ['A', 'B', 'M'];
  const drag = useDrag<Name>({
    svgRef,
    frame,
    ids,
    pointOf: (n) => pts[n],
    onDrag: (n, p) =>
      setPts((old) => {
        const q = snapPoint(p, VP, 1, 0.5);
        if (n === 'O') {
          // le cercle suit son centre : P se déplace d'autant
          // (refusé si P sortirait du cadre : le rayon changerait en douce)
          const free = { x: old.P.x + q.x - old.O.x, y: old.P.y + q.y - old.O.y };
          const P = snapPoint(free, VP, 1, 0.5);
          return P.x !== free.x || P.y !== free.y ? old : { ...old, O: q, P };
        }
        if (n === 'P' && q.x === old.O.x && q.y === old.O.y) return old;
        if (
          (n === 'A' && q.x === old.B.x && q.y === old.B.y) ||
          (n === 'B' && q.x === old.A.x && q.y === old.A.y)
        )
          return old;
        return { ...old, [n]: q };
      }),
  });

  const c =
    mode === 'center'
      ? { a: pts.O.x, b: pts.O.y, r2: (pts.P.x - pts.O.x) ** 2 + (pts.P.y - pts.O.y) ** 2 }
      : fromDiameter(pts.A, pts.B);
  const r = radiusExact(c.r2);
  const m = position(c.a, c.b, c.r2, pts.M);
  const px = (p: Pt) => ({ x: frame.tx(p.x), y: frame.ty(p.y) });
  const center = px({ x: c.a, y: c.b });
  const rPx = Math.sqrt(c.r2) * frame.unit;
  const pm = px(pts.M);
  const ma = { x: pts.A.x - pts.M.x, y: pts.A.y - pts.M.y };
  const mb = { x: pts.B.x - pts.M.x, y: pts.B.y - pts.M.y };
  const dot = ma.x * mb.x + ma.y * mb.y;
  // M confondu avec A ou B : pas d'angle ; M sur la droite (AB) : angle plat ou nul
  const atEnd = (ma.x === 0 && ma.y === 0) || (mb.x === 0 && mb.y === 0);
  const aligned = !atEnd && ma.x * mb.y - ma.y * mb.x === 0;
  const done = goal !== null && mode === 'center' && GOALS[goal]!.check(c.a, c.b, c.r2);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Mode"
          value={mode}
          options={[
            { value: 'center', label: 'Centre et rayon' },
            { value: 'diameter', label: 'Diamètre [AB]' },
          ]}
          onChange={(v) => {
            setMode(v);
            if (v === 'diameter') setGoal(null);
          }}
        />
        {mode === 'center' && (
          <ToggleChip on={goal !== null} onClick={() => setGoal((g) => (g === null ? 0 : null))}>
            Défi
          </ToggleChip>
        )}
      </div>

      {goal !== null && mode === 'center' && (
        <section
          className="bg-xp-50 space-y-2 rounded-2xl p-3"
          aria-live="polite"
          data-testid="cq-goal"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Défi {goal + 1}/{GOALS.length} · {GOALS[goal]!.text}
          </p>
          {done ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-success-600 text-sm font-bold">Réussi !</p>
              {goal + 1 < GOALS.length && (
                <button
                  type="button"
                  className={cn(PRIMARY_BUTTON, 'ml-auto')}
                  onClick={() => setGoal(goal + 1)}
                >
                  Défi suivant
                </button>
              )}
            </div>
          ) : (
            <p className="text-ink-600 text-sm">Déplace le centre Ω et le point P.</p>
          )}
        </section>
      )}

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label={`Cercle de centre (${formatNumber(c.a, 2)} ; ${formatNumber(c.b, 2)}) et de rayon ${r.plain}.`}
          data-testid="cq-figure"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} />
          <circle
            cx={center.x}
            cy={center.y}
            r={rPx}
            fill={CIRCLE}
            fillOpacity={0.06}
            stroke={CIRCLE}
            strokeWidth={2.5}
          />
          {mode === 'center' ? (
            <line
              x1={center.x}
              y1={center.y}
              x2={px(pts.P).x}
              y2={px(pts.P).y}
              stroke={CIRCLE}
              strokeWidth={2}
              strokeDasharray="5 4"
            />
          ) : (
            <g>
              <line
                x1={px(pts.A).x}
                y1={px(pts.A).y}
                x2={px(pts.B).x}
                y2={px(pts.B).y}
                stroke={CIRCLE}
                strokeWidth={2}
              />
              <line
                x1={pm.x}
                y1={pm.y}
                x2={px(pts.A).x}
                y2={px(pts.A).y}
                stroke={POS_COLOR[m.pos]}
                strokeWidth={1.5}
              />
              <line
                x1={pm.x}
                y1={pm.y}
                x2={px(pts.B).x}
                y2={px(pts.B).y}
                stroke={POS_COLOR[m.pos]}
                strokeWidth={1.5}
              />
              {m.pos === 'on' && !atEnd && (
                <RightAngleMark
                  x={pm.x}
                  y={pm.y}
                  ux={px(pts.A).x - pm.x}
                  uy={px(pts.A).y - pm.y}
                  vx={px(pts.B).x - pm.x}
                  vy={px(pts.B).y - pm.y}
                  color={POS_COLOR.on}
                />
              )}
            </g>
          )}
          <circle cx={center.x} cy={center.y} r={4} fill={CENTER} />
          {mode === 'diameter' && (
            <HaloText
              x={center.x + 7}
              y={center.y + 16}
              fontSize={14}
              fontWeight={700}
              fill={CENTER}
            >
              Ω
            </HaloText>
          )}
          {ids.map((n) => {
            const p = px(pts[n]);
            const label = n === 'O' ? 'Ω' : n;
            return (
              <DragHandle
                key={n}
                x={p.x}
                y={p.y}
                color={n === 'M' ? POS_COLOR[m.pos] : n === 'O' ? CENTER : CIRCLE}
                label={label}
                labelDx={p.x > SIZE - 24 ? -14 : 14}
                labelDy={p.y < 24 ? 16 : -14}
                name={`${n === 'O' ? 'Centre Ω' : `Point ${n}`} ${formatCoords(pts[n].x, pts[n].y)}`}
                active={drag.active === n}
                keyProps={drag.keyProps(n)}
              />
            );
          })}
        </svg>
      </div>

      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
        data-testid="cq-equation"
      >
        <MathText className="text-ink-900 block overflow-x-auto text-lg">{`$${canonicalTex(c.a, c.b, c.r2)}$`}</MathText>
        <p className="sr-only" aria-live="polite">
          Équation du cercle : {canonicalPlain(c.a, c.b, c.r2)}.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-ink-700 min-w-0 flex-1 text-sm">
            Centre Ω({formatNumber(c.a, 2)} ; {formatNumber(c.b, 2)}), rayon r = {r.plain}
            {r.plain.includes('√') ? ` ≈ ${formatNumber(r.value, 2)}` : ''} (r² ={' '}
            {formatNumber(c.r2, 2)}).
          </p>
          {onSend && (
            <SendToSheetButton
              label={r.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: r.atoms })}
            />
          )}
        </div>
        {mode === 'diameter' && (
          <MathText className="text-ink-700 block overflow-x-auto">
            {`$\\overrightarrow{MA} \\cdot \\overrightarrow{MB} = 0 \\iff ${diameterTex(pts.A, pts.B)}$`}
          </MathText>
        )}
        <ToggleChip on={developedOn} onClick={() => setDevelopedOn((v) => !v)}>
          Forme développée
        </ToggleChip>
        {developedOn && (
          <div className="space-y-1" data-testid="cq-developed">
            <p className="text-ink-600 text-sm">
              On développe, puis on retrouve le centre en complétant les carrés :
            </p>
            {completingSteps(c.a, c.b, c.r2).map((line, i) => (
              <MathText key={i} className="text-ink-900 block overflow-x-auto">
                {`$${i === 0 ? '' : '\\iff '}${line}$`}
              </MathText>
            ))}
          </div>
        )}
      </section>

      <section
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-3"
        data-testid="cq-point"
      >
        <MathText className="text-ink-900 block overflow-x-auto">
          {`$${diff(pts.M.x, c.a)} + ${diff(pts.M.y, c.b)} = ${dec(m.d2)} ${m.pos === 'on' ? '=' : m.pos === 'inside' ? '<' : '>'} ${dec(c.r2)}$`}
        </MathText>
        <p className={cn('text-sm font-semibold', POS_TEXT_CLASS[m.pos])} aria-live="polite">
          M{formatCoords(pts.M.x, pts.M.y)} est {POS_TEXT[m.pos]} cercle.
        </p>
        {mode === 'diameter' && (
          <p className="text-ink-700 text-sm" data-testid="cq-dot">
            <MathText>{`$\\overrightarrow{MA} \\cdot \\overrightarrow{MB} = ${dec(dot)}$`}</MathText>{' '}
            :{' '}
            {atEnd
              ? `nul, car M est confondu avec ${ma.x === 0 && ma.y === 0 ? 'A' : 'B'} (pas d’angle ici).`
              : aligned
                ? dot < 0
                  ? 'négatif : M est sur [AB], l’angle AMB est plat (M est dedans).'
                  : 'positif : M est sur la droite (AB) hors du segment, l’angle AMB est nul (M est dehors).'
                : dot === 0
                  ? 'nul, l’angle AMB est droit.'
                  : dot < 0
                    ? 'négatif, l’angle AMB est obtus (M est dedans).'
                    : 'positif, l’angle AMB est aigu (M est dehors).'}
          </p>
        )}
      </section>

      <Note live={false}>
        {mode === 'center'
          ? 'M(x ; y) est sur le cercle quand sa distance à Ω vaut r, soit ΩM² = r² : c’est l’équation (x − a)² + (y − b)² = r².'
          : 'Un point M est sur le cercle de diamètre [AB] exactement quand l’angle AMB est droit, c’est-à-dire quand le produit scalaire MA · MB est nul.'}
      </Note>
    </div>
  );
}
